import { get } from "lodash-es";
import React, { FC, ReactElement, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

import { Stack } from "@mui/material";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Tabs from "@mui/material/Tabs";

import Header from "../../components/Header";
import NamespaceRoleGuard from "../../components/NamespaceRoleGuard";
import Page from "../../components/Page";
import Tab from "../../components/styled/Tab";
import TabSection from "../../components/styled/TabSection";
import { useSelector } from "../../store";
import Billing from "./components/Billing";
import General from "./components/General";
import Invites from "./components/Invites";
import Team from "./components/Team";

type TabDefinition = {
  value: string;
  label: string;
  show: () => boolean;
  render: () => ReactElement;
};

const View: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organizationId } = useSelector((state) => state.current);
  const { byId } = useSelector((state) => state.organizations);
  const { rootId } = useSelector((state) => state.namespaces);
  const tabValue = searchParams.get("tab") || "team";

  const handleChangeTab = (_: any, newValue: string) => {
    setSearchParams({ tab: newValue });
  };

  const tabs: TabDefinition[] = [
    {
      value: "general",
      label: "General",
      show: () => true,
      render: () => <General org={get(byId, organizationId, {})} />,
    },
    {
      value: "billing",
      label: "Billing",
      show: () => get(byId, organizationId, null)?.role === "owner",
      render: () => <Billing org={get(byId, organizationId, {})} />,
    },
    {
      value: "team",
      label: "Team",
      show: () => true,
      render: () => <Team />,
    },
    {
      value: "invites",
      label: "Invites",
      show: () => true,
      render: () => <Invites />,
    },
  ];

  useEffect(() => {
    if (
      (get(byId, organizationId + ".organizationType", "") as string) ===
      "personal"
    ) {
      navigate("/app/account", { replace: true });
    }
  }, [organizationId]);

  if (!Object.keys(byId).length) {
    return <></>;
  }

  return (
    <Page title="Organization Management">
      <Container maxWidth={false}>
        <Header breadcrumbs={false} title="Organization Settings" />
        <NamespaceRoleGuard namespaceId={rootId} role={"admin"} warn={true}>
          <Stack>
            <Box style={{ position: "sticky", top: 0 }}>
              <Tabs
                aria-labelledby={"organizationManageFormSectionTabs"}
                onChange={handleChangeTab}
                indicatorColor="primary"
                textColor="primary"
                selectionFollowsFocus={true}
                value={tabValue}
              >
                {tabs.map(
                  (t) =>
                    t.show() && (
                      <Tab
                        key={t.value}
                        label={t.label}
                        aria-controls={`tabpanel-${t.value}`}
                        id={`tab-${t.value}`}
                        disableFocusRipple={true}
                        disableRipple={true}
                        value={t.value}
                      />
                    ),
                )}
              </Tabs>
            </Box>
            <Container component={Paper} maxWidth={false} disableGutters={true}>
              {tabs.map(
                (t) =>
                  t.show() && (
                    <TabSection
                      key={t.value}
                      index={t.value}
                      value={tabValue}
                      title=""
                    >
                      {t.value === tabValue && t.render()}
                    </TabSection>
                  ),
              )}
            </Container>
          </Stack>
        </NamespaceRoleGuard>
      </Container>
    </Page>
  );
};

export default View;
